import api from '@/factories/api';
import Vue from 'vue';
import { normalizeTags, queryKey } from '@/components/pages/nodes/guideModel';

export default {
  namespaced: true,

  state: {
    tooltipsCategoryLoading: '',
    tooltipsCategories: {},
    guidesByTags: {},
    guidesLoadingKeys: {},
    whatsNewByTags: {},
    whatsNewLoadingKeys: {},
  },

  getters: {
    getTooltipsCategory(state) {
      return (category) => state.tooltipsCategories[category];
    },
    tooltipsCategoryLoading(state) {
      return state.tooltipsCategoryLoading;
    },
    getSingleTooltip(state) {
      return (category, type) => {
        return (state.tooltipsCategories[category] || []).find((i) => {
          return i.slug === type;
        });
      };
    },
    getGuidesByTags(state) {
      return (tags) => state.guidesByTags[queryKey(tags)] || [];
    },
    isGuidesLoading(state) {
      return (tags) => !!state.guidesLoadingKeys[queryKey(tags)];
    },
    getWhatsNewByTags(state) {
      return (tags) => state.whatsNewByTags[queryKey(tags)] || [];
    },
    isWhatsNewLoading(state) {
      return (tags) => !!state.whatsNewLoadingKeys[queryKey(tags)];
    },
  },

  mutations: {
    setTooltipCategory(state, data) {
      const { category, tooltips, resources } = data;

      tooltips.forEach((t) => {
        t.content.resources = (t.content?.resources || []).reduce(
          (acc, story) => {
            if (typeof story === 'string' && story) {
              story = (resources || []).find(({ uuid }) => {
                return story === uuid;
              });
            }

            if (story) {
              acc.push({
                name: story.name,
                url: 'https://conveyour.com/' + story.full_slug,
              });
            }

            return acc;
          },
          []
        );
      });

      Vue.set(state.tooltipsCategories, category, tooltips);
    },
    setTooltipsCategoryLoading(state, category) {
      state.tooltipsCategoryLoading = category;
    },
    setGuides(state, { tags, stories }) {
      Vue.set(state.guidesByTags, queryKey(tags), stories || []);
    },
    setGuidesLoading(state, { key, loading }) {
      if (loading) {
        Vue.set(state.guidesLoadingKeys, key, true);
        return;
      }
      Vue.delete(state.guidesLoadingKeys, key);
    },
    setWhatsNew(state, { tags, stories }) {
      Vue.set(state.whatsNewByTags, queryKey(tags), stories || []);
    },
    setWhatsNewLoading(state, { key, loading }) {
      if (loading) {
        Vue.set(state.whatsNewLoadingKeys, key, true);
        return;
      }
      Vue.delete(state.whatsNewLoadingKeys, key);
    },
  },

  actions: {
    async loadTooltips({ commit, getters }, category) {
      const categoryTooltips = getters.getTooltipsCategory(category);
      if (
        getters.tooltipsCategoryLoading === category ||
        (categoryTooltips && categoryTooltips.length)
      )
        return;

      commit('setTooltipsCategoryLoading', category);
      try {
        const res = await api().get(`cms/tooltips?tags=${category}`);

        commit('setTooltipCategory', {
          category,
          tooltips: res?.stories || [],
          resources: res?.rels || [],
        });
      } catch (err) {
        throw err;
      } finally {
        commit('setTooltipsCategoryLoading', '');
      }
    },
    async loadGuides({ commit, getters }, tags) {
      const key = queryKey(tags);
      if (!key) return;
      if (getters.isGuidesLoading(tags)) return;
      if (getters.getGuidesByTags(tags).length) return;

      commit('setGuidesLoading', { key, loading: true });
      try {
        const res = await api().get(
          `cms/guides?tags=${encodeURIComponent(normalizeTags(tags).join(','))}`
        );
        commit('setGuides', {
          tags,
          stories: res?.stories || [],
        });
      } catch (err) {
        commit('setGuides', { tags, stories: [] });
      } finally {
        commit('setGuidesLoading', { key, loading: false });
      }
    },
    async loadWhatsNew({ commit, getters }, tags) {
      const key = queryKey(tags);
      if (!key) return;
      if (getters.isWhatsNewLoading(tags)) return;
      if (getters.getWhatsNewByTags(tags).length) return;

      commit('setWhatsNewLoading', { key, loading: true });
      try {
        const res = await api().get(
          `cms/whatsNew?tags=${encodeURIComponent(
            normalizeTags(tags).join(',')
          )}`
        );
        commit('setWhatsNew', {
          tags,
          stories: res?.stories || [],
        });
      } catch (err) {
        commit('setWhatsNew', { tags, stories: [] });
      } finally {
        commit('setWhatsNewLoading', { key, loading: false });
      }
    },
  },
};
